
import { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Loader2 } from "lucide-react";
import { useInvestorProcessing } from "@/hooks/investor";
import UploadData from "./UploadData";
import InvestorsPage from "./InvestorsPage";
import ContactsPage from "./ContactsPage";

const DataWorkspace = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState(searchParams.get("tab") || "upload");
  const [isInitialDataLoading, setIsInitialDataLoading] = useState(true);
  const [dataLoadError, setDataLoadError] = useState<string | null>(null);
  
  console.log("DataWorkspace: Component mounted, starting data load");

  // Load data at the workspace level
  const {
    investors,
    contacts,
    businessInfo,
    relevanceContext,
    selectedInvestor,
    processedIds,
    isAnalyzing,
    isProcessingBatch,
    isEnriching,
    setSelectedInvestor,
    setIsEnriching,
    loadSavedData,
    forceReloadFromDatabase,
    handleProcessInvestor,
    handleEnrichInvestor,
    handleProcessBatch,
    handleInvestorUpdate,
    isInvestorProcessed,
    handleRelevanceSubmit,
    setRelevanceContext,
    isLoadingData
  } = useInvestorProcessing();

  // Update URL when tab changes
  const handleTabChange = (value: string) => {
    setActiveTab(value);
    setSearchParams({ tab: value });
  };

  // Update tab when URL changes
  useEffect(() => {
    const tab = searchParams.get("tab");
    if (tab && ["upload", "investors", "contacts"].includes(tab)) {
      setActiveTab(tab);
    }
  }, [searchParams]);

  // Load data on initial mount with error handling
  useEffect(() => {
    const loadData = async () => {
      if (!isInitialDataLoading) return;
      
      try {
        console.log("DataWorkspace: Starting initial data load");
        setDataLoadError(null);
        
        await loadSavedData();
        console.log("DataWorkspace: Initial data load completed successfully");
        
      } catch (error) {
        console.error("DataWorkspace: Error loading initial data:", error);
        setDataLoadError("Failed to load data. Please try refreshing the page.");
      } finally {
        setIsInitialDataLoading(false);
        console.log("DataWorkspace: Initial data loading finished");
      }
    };
    
    loadData();
  }, [loadSavedData, isInitialDataLoading]);

  // Show loading state only for initial data load
  if (isInitialDataLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center space-y-4">
          <Loader2 className="h-8 w-8 animate-spin text-blue-600 mx-auto" />
          <p className="text-sm text-gray-600">Loading data workspace...</p>
        </div>
      </div>
    );
  }

  // Show error state if data loading failed
  if (dataLoadError) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center space-y-4">
          <p className="text-sm text-red-600">{dataLoadError}</p>
          <button 
            onClick={() => {
              setDataLoadError(null);
              setIsInitialDataLoading(true);
            }}
            className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold tracking-tight">Data Workspace</h1>
        <p className="text-muted-foreground">
          Manage your investor data, upload files, process contacts, and analyze opportunities in one unified workspace.
        </p>
      </div>

      <Tabs value={activeTab} onValueChange={handleTabChange} className="space-y-6">
        <TabsList className="grid w-full grid-cols-3">
          <TabsTrigger value="upload">Upload & Setup</TabsTrigger>
          <TabsTrigger value="investors">Investors</TabsTrigger>
          <TabsTrigger value="contacts">Contacts</TabsTrigger>
        </TabsList>
        
        <TabsContent value="upload" className="space-y-6">
          <UploadData />
        </TabsContent>
        
        <TabsContent value="investors" className="space-y-6">
          <InvestorsPage 
            investors={investors}
            contacts={contacts}
            businessInfo={businessInfo}
            relevanceContext={relevanceContext}
            selectedInvestor={selectedInvestor}
            processedIds={processedIds}
            isAnalyzing={isAnalyzing}
            isProcessingBatch={isProcessingBatch}
            isEnriching={isEnriching}
            setSelectedInvestor={setSelectedInvestor}
            setIsEnriching={setIsEnriching}
            forceReloadFromDatabase={forceReloadFromDatabase}
            handleProcessInvestor={handleProcessInvestor}
            handleEnrichInvestor={handleEnrichInvestor}
            handleProcessBatch={handleProcessBatch}
            handleInvestorUpdate={handleInvestorUpdate}
            isInvestorProcessed={isInvestorProcessed}
            handleRelevanceSubmit={handleRelevanceSubmit}
            setRelevanceContext={setRelevanceContext}
            isLoadingData={isLoadingData}
          />
        </TabsContent>
        
        <TabsContent value="contacts" className="space-y-6">
          <ContactsPage 
            contacts={contacts}
            investors={investors}
          />
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default DataWorkspace;
